<template>
  <div class="ai-check-container">
    <!-- Header Banner -->
    <div class="ai-header-banner">
      <div class="banner-overlay"></div>
      <div class="banner-content">
        <div class="banner-left">
          <div class="ai-icon-pulse">
            <i class="el-icon-connection"></i>
          </div>
          <div>
            <h1 class="banner-title">QUẢN LÝ THIẾT BỊ NHẬN DIỆN AI</h1>
            <p class="banner-subtitle">Quản lý danh sách, cấu hình kết nối, đăng ký Webhook và đồng bộ trạng thái các thiết bị AI.</p>
          </div>
        </div>
        <div class="banner-actions flex gap-2">
          <el-button type="success" class="gradient-btn green-btn" icon="el-icon-plus" @click="handleAddDevices">
            Thêm thiết bị
          </el-button>
          <el-button type="primary" class="gradient-btn blue-btn" icon="el-icon-refresh" @click="handleCheckStatus">
            Kiểm tra trạng thái
          </el-button>
          <el-button type="warning" class="gradient-btn purple-btn" icon="el-icon-share" @click="registerWebhookManyDevice">
            Đăng ký Webhook
          </el-button>
        </div>
      </div>
    </div>

    <!-- Quick Stats Grid -->
    <div class="ai-stats-grid">
      <div class="stat-card blue">
        <div class="stat-icon"><i class="el-icon-monitor"></i></div>
        <div class="stat-info">
          <p class="stat-label">Tổng số thiết bị</p>
          <h3 class="stat-value">{{ totalRecords }}</h3>
        </div>
      </div>
      <div class="stat-card green">
        <div class="stat-icon"><i class="el-icon-circle-check"></i></div>
        <div class="stat-info">
          <p class="stat-label">Đang hoạt động (Online)</p>
          <h3 class="stat-value">{{ stats.onlineCount }}</h3>
        </div>
      </div>
      <div class="stat-card purple">
        <div class="stat-icon"><i class="el-icon-link"></i></div>
        <div class="stat-info">
          <p class="stat-label">Đã đăng ký Webhook</p>
          <h3 class="stat-value">{{ stats.webhookCount }}</h3>
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="glass-card mb-4">
      <el-form :inline="true" :model="dataFilter" class="modern-filter-form">
        <el-form-item label="Trường học">
          <el-select
            v-model="dataFilter.idSchool"
            filterable
            clearable
            placeholder="Tất cả các trường"
            class="modern-select"
            @change="searchFindAllDevices"
          >
            <el-option
              v-for="item in schoolList"
              :key="item.id"
              :value="item.id"
              :label="`${item.id} - ${item.schoolName}`"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Trạng thái">
          <el-select
            v-model="dataFilter.active"
            clearable
            placeholder="Tất cả trạng thái"
            class="modern-select"
            @change="searchFindAllDevices"
          >
            <el-option label="Online" :value="true" />
            <el-option label="Offline" :value="false" />
          </el-select>
        </el-form-item>

        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" @click="searchFindAllDevices">Lọc dữ liệu</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- Main Table -->
    <div class="glass-card table-container">
      <el-table
        ref="multipleTable"
        :data="dataTable"
        v-loading="loading.table"
        element-loading-text="Đang lấy dữ liệu thiết bị..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(255,255,255,0.7)"
        class="modern-table"
        border
        highlight-current-row
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center" />
        
        <el-table-column type="index" :index="calculateStt" label="STT" width="55" align="center" />

        <el-table-column label="Tên trường" min-width="220">
          <template slot-scope="{ row }">
            <span class="school-name-text" @click="updateDevice(row, true)">{{ row.nameSchool }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Tên thiết bị" min-width="180">
          <template slot-scope="{ row }">
            <span class="device-name-text">
              <i class="el-icon-s-platform" style="color: #64748b; margin-right: 4px;"></i>
              {{ row.nameDevice }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Hãng" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" type="info" effect="plain">{{ row.typeDevice || 'Hik' }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Thông tin kết nối" min-width="260">
          <template slot-scope="{ row }">
            <div class="info-list">
              <div><span class="info-lbl">Tên miền:</span> <strong>{{ row.schoolDomain || '—' }}</strong></div>
              <div><span class="info-lbl">IP Local:</span> <strong>{{ row.ipLocal || '—' }}</strong></div>
              <div><span class="info-lbl">Port Local / HTTP:</span> <strong>{{ row.port || '—' }}</strong></div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Thông tin tài khoản" min-width="180">
          <template slot-scope="{ row }">
            <div class="info-list">
              <div><span class="info-lbl">Acc:</span> <code>{{ row.adminAcc }}</code></div>
              <div><span class="info-lbl">Pass:</span> <code>{{ row.adminPassword }}</code></div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Hoạt động" width="110" align="center">
          <template slot-scope="{ row }">
            <span class="status-badge" :class="row.active ? 'online' : 'offline'">
              <span class="dot"></span>
              {{ row.active ? 'Online' : 'Offline' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Đăng ký Webhook" width="140" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.webhookRegister ? 'success' : 'danger'" size="medium" effect="light">
              {{ row.webhookRegister ? 'Đã đăng ký' : 'Chưa đăng ký' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Thông tin Modem" min-width="160">
          <template slot-scope="{ row }">
            <div class="info-list" v-if="row.modemAcc || row.modemPassword">
              <div><span class="info-lbl">Acc:</span> <code>{{ row.modemAcc }}</code></div>
              <div><span class="info-lbl">Pass:</span> <code>{{ row.modemPassword }}</code></div>
            </div>
            <span v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column label="Version điểm danh" width="130" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" type="warning" effect="plain">{{ row.versionAttendance || 'VERSION_1' }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Tác vụ" width="310" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="warning" size="mini" icon="el-icon-refresh" @click="openSynchronizeDevices(row)">
              Đồng bộ
            </el-button>
            <el-button type="primary" size="mini" icon="el-icon-edit" @click="updateDevice(row, false)">
              Sửa
            </el-button>
            <el-button type="danger" size="mini" icon="el-icon-delete" @click="deleteDevice(row)">
              Xóa
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination controls -->
      <div class="pagination-wrapper mt-3 flex justify-center">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentPageChange"
          :current-page.sync="dataFilter.page"
          :page-sizes="pageListDefaultNewOneAdmin"
          :page-size="dataFilter.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalRecords"
        >
        </el-pagination>
      </div>
    </div>

    <synchronize-devices-dialog ref="synchronizeDialog" @synchronized="findAllDevices" />

    <!-- Dialogs -->
    <AddDevice
      :is-open="isOpen.add"
      :schoolList="schoolList"
      @closeDialog="handleCloseDialogAdd"
    />

    <UpdateDevice
      :showDetail="showDetail"
      :idDevice="idDevice"
      :is-open="isOpen.update"
      @closeDialog="handleCloseDialogUpdate"
    />
  </div>
</template>

<script>
import SystemConfigService from "@/services/AdminService/SystemConfigService";
import AddDevice from "@/views/master/identify/dialog/AddDevice.vue";
import UpdateDevice from "@/views/master/identify/dialog/UpdateDevice.vue";
import SynchronizeDevicesDialog from "@/views/master/identify/dialog/SynchronizeDevicesDialog.vue";
import DeviceRecognitionService from "@/services/ManagerRecognition/DeviceRecognitionService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "ManagerDevices",
  components: { UpdateDevice, AddDevice, SynchronizeDevicesDialog },
  data() {
    return {
      idListDevice: [],
      dataTable: [],
      dataFilter: {
        idSchool: "",
        delActive: true,
        nameSchool: "",
        active: true,
        page: 1,
        limit: 25
      },
      schoolList: [],
      loading: {
        table: false
      },
      totalRecords: 0,
      isOpen: {
        add: false,
        update: false,
        detail: false
      },
      idDevice: 0,
      showDetail: false
    };
  },

  computed: {
    pageListDefaultNewOneAdmin() {
      return this.$pageListDefaultNewOneAdmin || [10, 25, 50, 100];
    },

    stats() {
      let onlineCount = 0;
      let webhookCount = 0;
      this.dataTable.forEach(d => {
        if (d.active) onlineCount++;
        if (d.webhookRegister) webhookCount++;
      });
      return { onlineCount, webhookCount };
    }
  },

  created() {
    this.findAllAppIconForSchool();
    this.findAllDevices();
  },

  methods: {
    async findAllDevices() {
      this.loading.table = true;
      try {
        const response = await DeviceRecognitionService.findAllDevices(
          this.dataFilter.idSchool,
          this.dataFilter.active,
          this.dataFilter.page,
          this.dataFilter.limit
        );
        this.dataTable = response.data.data.deviceRecognitionResponseList || [];
        this.totalRecords = response.data.data.total || 0;
      } catch (err) {
        console.error("Failed to load devices:", err);
      } finally {
        this.loading.table = false;
      }
    },

    searchFindAllDevices() {
      this.dataFilter.page = 1;
      this.findAllDevices();
    },

    async findAllAppIconForSchool() {
      try {
        const resp = await SystemConfigService.findSchoolConfig("", this.dataFilter.idSchool, true);
        if (this.dataFilter.idSchool === "") {
          this.schoolList = resp.data.data || [];
        }
      } catch (err) {
        console.error("Failed to load schools:", err);
      }
    },

    handleCloseDialogAdd() {
      this.isOpen.add = false;
      this.findAllDevices();
    },

    handleCloseDialogUpdate() {
      this.isOpen.update = false;
      this.findAllDevices();
    },

    handleSelectionChange(value) {
      this.idListDevice = value.map(item => item.id);
    },

    handleCurrentPageChange(value) {
      this.dataFilter.page = value;
      this.findAllDevices();
    },

    handleSizeChange(value) {
      this.dataFilter.limit = value;
      this.dataFilter.page = 1;
      this.findAllDevices();
    },

    calculateStt(index) {
      return (this.dataFilter.page - 1) * this.dataFilter.limit + index + 1;
    },

    handleAddDevices() {
      this.isOpen.add = true;
    },

    handleCheckStatus() {
      if (this.idListDevice.length === 0) {
        NotifyMessage.leftCornerNotificationError("Chưa có thiết bị nào được chọn!");
        return;
      }
      
      DeviceRecognitionService.checkConnectionDevice(this.idListDevice).then((response) => {
        if (response.data.status === 200) {
          NotifyMessage.leftCornerNotificationSuccess("Kiểm tra thành công!");
          this.findAllDevices();
        }
      }).catch((error) => {
        console.error(error);
        NotifyMessage.leftCornerNotificationError("Kiểm tra thất bại!");
      });
    },

    registerWebhookManyDevice() {
      if (this.idListDevice.length === 0) {
        NotifyMessage.leftCornerNotificationError("Chưa có thiết bị nào được chọn!");
        return;
      }

      DeviceRecognitionService.registerWebhookManyDevice(this.idListDevice).then((response) => {
        if (response.data.status === 200) {
          NotifyMessage.leftCornerNotificationSuccess("Đăng ký thành công!");
          this.findAllDevices();
        }
      }).catch((error) => {
        console.error(error);
        NotifyMessage.leftCornerNotificationError("Kiểm tra thất bại!");
      });
    },

    openSynchronizeDevices(row) {
      this.$refs.synchronizeDialog.open(row);
    },

    updateDevice(row, value) {
      this.showDetail = value;
      this.idDevice = row.id;
      this.isOpen.update = true;
    },

    deleteDevice(row) {
      this.idDevice = row.id;
      this.$confirm(
        "Bạn có chắc chắn muốn xóa thiết bị đã chọn?",
        "Thông báo!",
        {
          distinguishCancelAndClose: true,
          confirmButtonText: "Có",
          closeOnClickModal: false,
          cancelButtonText: "Không",
          type: "warning"
        }
      ).then(() => {
        DeviceRecognitionService.deleteDeviceRecognition(this.idDevice).then((response) => {
          if (response.data.status === 200 && response.data.data === true) {
            NotifyMessage.leftCornerNotificationSuccess("Xóa thiết bị thành công!");
          } else {
            NotifyMessage.leftCornerNotificationError("Xóa thiết bị thất bại!");
          }
          this.findAllDevices();
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-check-container {
  padding: 16px;
  background-color: #f4f6fa;
  min-height: calc(100vh - 120px);
}

/* Header Banner Styling */
.ai-header-banner {
  position: relative;
  border-radius: 12px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 24px 32px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(30, 60, 114, 0.15);

  .banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  }

  .banner-content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
    width: 100%;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .ai-icon-pulse {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
    animation: iconPulse 3s infinite ease-in-out;

    i {
      font-size: 32px;
      color: #fff;
    }
  }

  .banner-title {
    color: #fff;
    font-size: 22px;
    margin: 0 0 6px 0;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .banner-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    margin: 0;
  }

  .banner-actions {
    display: flex;
    gap: 10px;
  }

  .gradient-btn {
    border: none;
    font-weight: 600;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-2px);
    }

    &.green-btn {
      background: linear-gradient(90deg, #10b981 0%, #059669 100%);
      box-shadow: 0 4px 10px rgba(16, 185, 129, 0.3);
      &:hover { box-shadow: 0 6px 15px rgba(16, 185, 129, 0.4); }
    }

    &.blue-btn {
      background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
      box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
      &:hover { box-shadow: 0 6px 15px rgba(59, 130, 246, 0.4); }
    }

    &.purple-btn {
      background: linear-gradient(90deg, #8b5cf6 0%, #6d28d9 100%);
      box-shadow: 0 4px 10px rgba(139, 92, 246, 0.3);
      &:hover { box-shadow: 0 6px 15px rgba(139, 92, 246, 0.4); }
    }
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.4);
  }
}

/* Stats Cards Grid */
.ai-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
  }

  .stat-info {
    flex: 1;
  }

  .stat-label {
    margin: 0 0 4px 0;
    font-size: 13px;
    color: #64748b;
  }

  .stat-value {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
  }

  /* Colors */
  &.blue {
    border-left: 4px solid #3b82f6;
    .stat-icon { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
  }
  &.green {
    border-left: 4px solid #10b981;
    .stat-icon { background: rgba(16, 185, 129, 0.1); color: #10b981; }
  }
  &.purple {
    border-left: 4px solid #8b5cf6;
    .stat-icon { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
  }
}

/* Glassmorphic Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.table-container {
  background: #fff;
  padding: 12px;
}

/* Modern Form Filters */
.modern-filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;

  /deep/ .el-form-item {
    margin-bottom: 0 !important;
    margin-right: 0 !important;
  }

  /deep/ .el-form-item__label {
    font-weight: 600;
    color: #475569;
    padding-bottom: 6px;
  }

  .modern-select {
    width: 220px;
  }
}

/* Modern Table Styles */
.modern-table {
  border-radius: 8px;
  overflow: hidden;

  /deep/ th {
    background-color: #f8fafc !important;
    color: #475569;
    font-weight: 600;
    height: 48px;
  }

  /deep/ td {
    padding: 10px 0;
  }
}

/* Cell styles */
.school-name-text {
  font-weight: 600;
  color: #3b82f6;
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
}

.device-name-text {
  font-weight: 600;
  color: #1e293b;
}

.info-list {
  font-size: 12px;
  color: #64748b;
  line-height: 1.6;

  .info-lbl {
    color: #94a3b8;
  }
  code {
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: Consolas, Monaco, monospace;
    font-size: 11px;
    color: #e11d48;
  }
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  &.online {
    background-color: #f0fdf4;
    color: #166534;
    .dot {
      background-color: #22c55e;
      box-shadow: 0 0 8px #22c55e;
      animation: pulseGreen 2s infinite;
    }
  }

  &.offline {
    background-color: #fef2f2;
    color: #991b1b;
    .dot {
      background-color: #ef4444;
    }
  }
}

@keyframes pulseGreen {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}
</style>